import React, { useEffect, useState } from "react";
import Header from "../components/Admin/header/Header";
import Sidebar from "../components/Admin/Sidebar/Sidebar";
import {
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Container,
  FormControlLabel,
  Grid,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { toast } from "react-toastify";
import { useNavigate, useParams } from "react-router-dom";
import {
  createSupplierApi,
  getSupplierByIdApi,
  updateSupplierApi,
} from "../services/supplier.service";
import { useGetApi } from "../hooks/useGetApi";
import ErrorMessage from "../components/ErrorMessage/errorMessage";
import Loader from "../components/Loader/loader";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";

export default function MasterSupplierAdd() {
  const { id } = useParams();
  const navigate = useNavigate();

  const handleClose = () => {
    navigate(-1);
  };

  const initialState = {
    supplierName: "",
    contactPerson: "",
    contactNo: "",
    email: "",
    address: "",
    city: "",
    state: "",
    pincode: "",
    gstNo: "",
    panNo: "",
    bankDetails: {
      bankName: "",
      accountNo: "",
      ifscCode: "",
    },
    active: true,
  };

  const [formData, setFormData] = useState(initialState);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [selectedRowLoading, setSelectedRowLoading] = useState(false);
  const [selectedRowError, setSelectedRowError] = useState(false);

  // Fetch supplier data for editing
  useEffect(() => {
    if (id) {
      const fetchSupplier = async () => {
        setSelectedRowLoading(true);
        setSelectedRowError(false);
        try {
          console.log("Fetching supplier with ID:", id);
          const response = await getSupplierByIdApi(id);
          console.log("Supplier fetch response:", response);
          
          if (response?.isSuccess || response?.success) {
            setSelectedRow(response.data);
            console.log("Supplier data set:", response.data);
          } else {
            console.error("Failed to fetch supplier:", response?.message);
            setSelectedRowError(true);
          }
        } catch (error) {
          console.error("Error fetching supplier:", error);
          setSelectedRowError(true);
        } finally {
          setSelectedRowLoading(false);
        }
      };
      
      fetchSupplier();
    }
  }, [id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name.startsWith('bankDetails.')) {
      const bankField = name.split('.')[1];
      setFormData((prevState) => ({
        ...prevState,
        bankDetails: {
          ...prevState.bankDetails,
          [bankField]: value,
        },
      }));
    } else {
      setFormData((prevState) => ({
        ...prevState,
        [name]: type === "checkbox" ? checked : value,
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    let response;

    // Basic validation
    if (!formData.supplierName?.trim()) {
      toast.error("Supplier name is required");
      return;
    }

    if (!formData.contactPerson?.trim()) {
      toast.error("Contact person is required");
      return;
    }

    if (!formData.contactNo?.trim()) {
      toast.error("Contact number is required");
      return;
    }

    setIsLoading(true);

    try {
      if (id) {
        // Update existing supplier
        response = await updateSupplierApi(id, formData);
      } else {
        // Create new supplier
        response = await createSupplierApi(formData);
      }

      // Handle both success and isSuccess response formats
      if (response?.success || response?.isSuccess) {
        toast.success(
          `Supplier ${id ? "updated" : "added"} successfully`
        );
        // Navigate back to supplier list with a timestamp to force refresh
        navigate("/master/supplier", { replace: true });
      } else {
        toast.error(response?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error:", error);
      toast.error("An error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    console.log("Edit supplier debug:", { id, selectedRow, selectedRowLoading, selectedRowError });
    if (selectedRow && id) {
      console.log("Populating form with supplier data:", selectedRow);
      setFormData({
        supplierName: selectedRow?.supplierName || "",
        contactPerson: selectedRow?.contactPerson || "",
        contactNo: selectedRow?.contactNo || "",
        email: selectedRow?.email || "",
        address: selectedRow?.address || "",
        city: selectedRow?.city || "",
        state: selectedRow?.state || "",
        pincode: selectedRow?.pincode || "",
        gstNo: selectedRow?.gstNo || "",
        panNo: selectedRow?.panNo || "",
        bankDetails: {
          bankName: selectedRow?.bankDetails?.bankName || "",
          accountNo: selectedRow?.bankDetails?.accountNo || "",
          ifscCode: selectedRow?.bankDetails?.ifscCode || "",
        },
        active: selectedRow?.active ?? true,
      });
    }
  }, [selectedRow, id]);

  if (selectedRowLoading) {
    return <Loader />;
  }

  if (selectedRowError) {
    return <ErrorMessage />;
  }

  return (
    <Container maxWidth={false} disableGutters>
      <Header />
      <Box sx={{ display: "flex" }}>
        <Box>
          <Sidebar />
        </Box>
        <Box sx={{ width: "100%", pr: "90px" }}>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(-1)}
            sx={{
              color: '#5D6A82', 
              textTransform: 'none', 
              fontSize: '16px',
              fontWeight: 'bold',
              padding: 0,
              transition: 'none !important',
              mt: 2
            }}
          >
            Back
          </Button>
          {/* Heading */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: "20px",
              color: "#5D6A82",
            }}
          >
            <Typography sx={{ color: "inherit" }}>
              {id ? "Edit Supplier" : "Add Supplier"}
            </Typography>
            <IconButton onClick={handleClose}>
              <CloseIcon sx={{ color: "inherit" }} />
            </IconButton>
          </Box>

          {/* Form */}
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={3}>
              {/* Basic Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mb: 2, color: "#5D6A82" }}>
                  Basic Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Supplier Name *"
                  name="supplierName"
                  value={formData?.supplierName || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Person *"
                  name="contactPerson"
                  value={formData?.contactPerson || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Contact Number *"
                  name="contactNo"
                  value={formData?.contactNo || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email"
                  name="email"
                  type="email"
                  value={formData?.email || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Address Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, color: "#5D6A82" }}>
                  Address Information
                </Typography>
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  name="address"
                  multiline
                  rows={2}
                  value={formData?.address || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="City"
                  name="city"
                  value={formData?.city || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="State"
                  name="state"
                  value={formData?.state || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Pincode"
                  name="pincode"
                  value={formData?.pincode || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Tax Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, color: "#5D6A82" }}>
                  Tax Information
                </Typography>
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="GST Number"
                  name="gstNo"
                  value={formData?.gstNo || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="PAN Number"
                  name="panNo"
                  value={formData?.panNo || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Bank Details */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ mt: 2, mb: 2, color: "#5D6A82" }}>
                  Bank Details
                </Typography>
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Bank Name"
                  name="bankDetails.bankName"
                  value={formData?.bankDetails?.bankName || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="Account Number"
                  name="bankDetails.accountNo"
                  value={formData?.bankDetails?.accountNo || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} md={4}>
                <TextField
                  fullWidth
                  label="IFSC Code"
                  name="bankDetails.ifscCode"
                  value={formData?.bankDetails?.ifscCode || ""}
                  onChange={handleChange}
                  size="small"
                />
              </Grid>

              {/* Status */}
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formData?.active || false}
                      onChange={handleChange}
                      name="active"
                    />
                  }
                  label="Active"
                />
              </Grid>

              {/* Submit Button */}
              <Grid item xs={12}>
                <Box sx={{ display: "flex", justifyContent: "center", mt: "20px", mb: "40px" }}>
                  <Button
                    variant="contained"
                    type="submit"
                    sx={{
                      textTransform: "none",
                      backgroundColor: "rgba(1, 121, 176, 1)",
                      width: "150px",
                    }}
                    disabled={isLoading}
                  >
                    {isLoading ? (
                      <CircularProgress size={24} />
                    ) : id ? (
                      "Update"
                    ) : (
                      "Register"
                    )}
                  </Button>
                </Box>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </Container>
  );
} 